import { IPage } from '../entities/page';
import { IAddShardRepositoryRequest, IShardRepositoryRequestParams, IShardRepositoryTestConnectionRequest, IShardRepositoryTestConnectionResponse, IShardRepository, IUpdateShardRepositoryRequest, StorageType } from "../entities/shard-repository";
import { pick } from "underscore";
import { storageUtils } from 'utils/storage-utils';
import { DataService } from "./data-service";
import { IEntityMetricsResponse } from 'api/entities/metrics';
import { metricsServiceHelper } from "./metrics-service-helper";
import { API_ROUTES } from "../../constants/routes";

type TestConnectionRequest = IShardRepositoryTestConnectionRequest | IAddShardRepositoryRequest | IUpdateShardRepositoryRequest;

class ShardRepositoryService extends DataService {
    constructor() {
        super(API_ROUTES.shardRepository);
    }

    get(id: string): Promise<IShardRepository> {
        return this.api.get<IShardRepository>(id);
    }

    getPage(requestParams: IShardRepositoryRequestParams): Promise<IPage<IShardRepository>> {
        return this.api.get<IPage<IShardRepository>>(null, requestParams);
    }

    getMetrics(id: string): Promise<IEntityMetricsResponse[]>
    getMetrics(ids: string[]): Promise<IEntityMetricsResponse[]>
    async getMetrics(ids: string | string[]): Promise<IEntityMetricsResponse[]> {
        return metricsServiceHelper.getMetrics(this.api, ids);
    }

    delete(id: string): Promise<void> {
        return this.api.delete(id);
    }

    addStorageNode(addRequest: IAddShardRepositoryRequest): Promise<IShardRepository> {
        return this.api.post<IShardRepository>(null, this.encodeEndpointUrl(addRequest));
    }

    update(updateRequest: IUpdateShardRepositoryRequest): Promise<IShardRepository> {
        return this.api.put<IShardRepository>(updateRequest.id, this.encodeEndpointUrl(updateRequest));
    }

    bucketNameValidator(bucketName: string, storageType: StorageType, endpointURL?: string): Promise<boolean> {
        if (!storageType) {
            return Promise.resolve(true);
        }

        return this.getPage({ q: bucketName, strict: true, fields: ['id', 'bucket', 'endpointURL'], storageType, deleted: false })
            .then(page => page.count === 0
                || (storageType === "s3p" && !page.data.some(x => x.endpointURL === endpointURL)));
    }

    getStorageNodeState(id: string) {
        let storageNodeItem = sessionStorage.getItem(id);

        if (!storageNodeItem) {
            return null;
        }

        return JSON.parse(storageNodeItem);
    }

    setStorageNodeState(id: string, storageNodeRawData: any) {
        let sensitiveStorageNodeData =  {
            ...pick(storageNodeRawData, [
                "id",
                "type",
                "key",
                "secret",
                "project_id",
                "client_email",
                "sasUri"
            ]),
            type: storageUtils.getStorageProviderTypeGeneralizedType(storageNodeRawData.type)
        };

        sessionStorage.setItem(id, JSON.stringify(sensitiveStorageNodeData));
    }

    testConnection(request: IShardRepositoryTestConnectionRequest): Promise<IShardRepositoryTestConnectionResponse> {
        return this.api.post<IShardRepositoryTestConnectionResponse>("testConnection", this.extractProps(this.encodeEndpointUrl(request)));
    }

    protected encodeEndpointUrl(request: TestConnectionRequest): TestConnectionRequest {
        return {...request, endpointURL: this.encodeUri(request.endpointURL)}
    }

    protected extractProps(request: TestConnectionRequest): TestConnectionRequest {
        let extractedProps = {};
        if (request.storageType === "GCS" && !!request.gcsConfigProps) {
            extractedProps = {
                secret: request.gcsConfigProps.private_key,
                project_id: request.gcsConfigProps.project_id,
                client_email: request.gcsConfigProps.client_email
            }
        }

        return {...request, ...extractedProps};
    }
}

export const shardRepositoryService = new ShardRepositoryService();
